import OperationsAPI from 'dashboard/api/operations';

export const types = {
  SET_OVERVIEW: 'SET_OPERATIONS_OVERVIEW',
  SET_AI: 'SET_OPERATIONS_AI',
  SET_CHANNELS: 'SET_OPERATIONS_CHANNELS',
  SET_TEAMS: 'SET_OPERATIONS_TEAMS',
  SET_QUEUE: 'SET_OPERATIONS_QUEUE',
  SET_PERIOD: 'SET_OPERATIONS_PERIOD',
  SET_UI_FLAG: 'SET_OPERATIONS_UI_FLAG',
  SET_ACTIVE: 'SET_OPERATIONS_ACTIVE',
  SET_TEAM_DRAWER: 'SET_OPERATIONS_TEAM_DRAWER',
  SET_TEAM_DRAWER_DATA: 'SET_OPERATIONS_TEAM_DRAWER_DATA',
  SET_COVERAGE: 'SET_OPERATIONS_COVERAGE',
};

export const ALL_BLOCKS = ['overview', 'ai', 'channels', 'teams', 'queue'];

// Evento do cable é GATILHO, não dado: o painel mostra agregados que o evento
// não carrega, então cada evento vira refetch do bloco afetado.
const SOURCE_BLOCKS = {
  // channels incluso (2026-08-24): os cards por inbox mostram abertas/aguardando
  // — resolver conversa congelava o número até o próximo mount (caso conv 163).
  conversations: ['overview', 'teams', 'queue', 'channels'],
  presence: ['teams'],
  ai: ['ai'],
  // Presença de ramal (AMI ContactStatus) mexe só no card de Voz, que vive
  // dentro do payload de channels.
  voice: ['channels'],
};

// `this` importa (ApiClient monta a URL a partir da instância) — chamar por
// referência solta (`OperationsAPI.overview`) quebraria.
const BLOCK_REQUESTS = {
  overview: params => OperationsAPI.overview(params),
  ai: params => OperationsAPI.ai(params),
  channels: params => OperationsAPI.channels(params),
  teams: params => OperationsAPI.teams(params),
  queue: params => OperationsAPI.queue(params),
};

const BLOCK_MUTATIONS = {
  overview: types.SET_OVERVIEW,
  ai: types.SET_AI,
  channels: types.SET_CHANNELS,
  teams: types.SET_TEAMS,
  queue: types.SET_QUEUE,
};

// Mesma mecânica do fetchConversationStats (helper/actionCable.js): leading +
// trailing. Ação isolada refetcha na hora; rajada de eventos (campanha, bulk
// resolve) colapsa em 1 request no início e 1 no fim. Janela de 10s porque os
// blocos custam SEGUNDOS de servidor cada (overview ~3s em conta grande): com
// 2s o painel gerava carga mais rápido do que o backend respondia e saturou o
// Puma de dev inteiro — toda página do app travava (2026-08-24, conta 10).
export const NUDGE_WINDOW_MS = 10000;

const throttle = { timers: {}, lastAt: {} };

// Usado pelo deactivate (página desmontou) e pelos specs — timer pendente de
// uma página já fechada não pode disparar request.
export const resetRealtimeThrottle = () => {
  Object.values(throttle.timers).forEach(timer => clearTimeout(timer));
  throttle.timers = {};
  throttle.lastAt = {};
};

const buildParams = period => {
  if (period.mode === 'realtime') {
    // "Hoje até agora" no fuso do USUÁRIO. Sem params o backend cai no default
    // (meia-noite UTC = 21:00 BRT de ontem) e o "hoje" do Tempo Real divergia
    // do "hoje" do botão Hoje (que já manda meia-noite local).
    const startOfDay = new Date();
    startOfDay.setHours(0, 0, 0, 0);
    return {
      since: String(Math.floor(startOfDay.getTime() / 1000)),
      until: String(Math.floor(Date.now() / 1000)),
    };
  }

  const params = {};
  if (period.from) params.since = String(period.from);
  if (period.to) params.until = String(period.to);
  return params;
};

const errorMessage = error =>
  error?.response?.data?.error || error?.message || String(error);

const initialPeriod = () => ({ mode: 'realtime', from: null, to: null });

const initialTeamDrawer = () => ({
  teamId: null,
  data: null,
  isFetching: false,
});

const state = {
  overview: null,
  // Período contado na fonte (lento)? { counted_live, reason, ready_from,
  // complete_until } | { failed: true } | null — o banner do Relatório de
  // Conversas (ReportLiveCountBanner) lê o mesmo formato.
  coverage: null,
  ai: null,
  // { inboxes: [...], voice: null|{error}|{...}, telephony: null|{...} }
  channels: null,
  teams: [],
  queue: null,
  period: initialPeriod(),
  // Drawer de agentes de UM time. teamId null = fechado.
  teamDrawer: initialTeamDrawer(),
  uiFlags: {
    isFetching: false,
    lastError: null,
    isStale: false,
  },
  active: false,
};

const getters = {
  getOverview: $state => $state.overview,
  getCoverage: $state => $state.coverage,
  getAi: $state => $state.ai,
  getChannels: $state => $state.channels,
  getTeams: $state => $state.teams,
  getQueue: $state => $state.queue,
  getPeriod: $state => $state.period,
  getTeamDrawer: $state => $state.teamDrawer,
  getUIFlags: $state => $state.uiFlags,
  isActive: $state => $state.active,
};

const actions = {
  activate({ commit, dispatch }) {
    commit(types.SET_ACTIVE, true);
    dispatch('fetchCoverage');
    return dispatch('fetchAll');
  },

  deactivate({ commit }) {
    resetRealtimeThrottle();
    // Página desmontou: o drawer não pode sobreviver pra reabrir sozinho no
    // próximo mount com dado velho de outro período.
    commit(types.SET_TEAM_DRAWER, initialTeamDrawer());
    commit(types.SET_ACTIVE, false);
  },

  setPeriod({ commit, dispatch }, period) {
    resetRealtimeThrottle();
    commit(types.SET_PERIOD, period);
    dispatch('fetchCoverage');
    return dispatch('fetchAll');
  },

  // Só quando o período muda (abrir a página, trocar período): o nudge de
  // tempo real não muda a cobertura. Em paralelo com os blocos, pra o aviso
  // aparecer ENQUANTO a contagem lenta roda. Falha vira a mesma linha neutra
  // do ReportLiveCountBanner (`{ failed: true }`) e vai pro console.
  async fetchCoverage({ commit, state: $state }) {
    commit(types.SET_COVERAGE, null);
    try {
      const { data } = await OperationsAPI.coverage(buildParams($state.period));
      commit(types.SET_COVERAGE, data.rollup);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[operationsDashboard] coverage falhou', error);
      commit(types.SET_COVERAGE, { failed: true });
    }
  },

  fetchAll({ dispatch }) {
    return dispatch('fetchBlocks', ALL_BLOCKS);
  },

  // allSettled (não Promise.all): bloco que respondeu entra, bloco que falhou
  // MANTÉM o dado anterior e a página grita via isStale/lastError. Perder o que
  // deu certo por causa do que falhou seria degradação silenciosa ao contrário.
  async fetchBlocks({ commit, state: $state }, blocks) {
    const params = buildParams($state.period);
    commit(types.SET_UI_FLAG, { isFetching: true });

    const entries = blocks.map(block => ({
      request: () => BLOCK_REQUESTS[block](params),
      apply: data => commit(BLOCK_MUTATIONS[block], data),
    }));

    // Drawer aberto pega CARONA no mesmo lote que atualiza os cards de time:
    // o nudge de presença já cadencia isso, então não há throttle novo pra
    // manter — e o drawer nunca fica mostrando presença de minutos atrás.
    const drawerTeamId = blocks.includes('teams')
      ? $state.teamDrawer.teamId
      : null;
    if (drawerTeamId) {
      entries.push({
        request: () =>
          OperationsAPI.teamAgents({ ...params, team_id: drawerTeamId }),
        apply: data =>
          commit(types.SET_TEAM_DRAWER_DATA, { teamId: drawerTeamId, data }),
      });
    }

    const results = await Promise.allSettled(
      entries.map(entry => entry.request())
    );

    const failure = results.find(result => result.status === 'rejected');
    results.forEach((result, index) => {
      if (result.status !== 'fulfilled') return;
      entries[index].apply(result.value.data);
    });

    commit(types.SET_UI_FLAG, {
      isFetching: false,
      isStale: Boolean(failure),
      // Objeto novo a cada falha: a página observa a referência pra decidir
      // se mostra o toast (mesma mensagem duas vezes ainda é erro duas vezes).
      lastError: failure
        ? { message: errorMessage(failure.reason), at: Date.now() }
        : null,
    });
  },

  // Abre o drawer e busca na hora. As atualizações seguintes vêm de carona no
  // fetchBlocks (bloco 'teams'), não de um poll próprio.
  async openTeamDrawer({ commit, state: $state }, teamId) {
    commit(types.SET_TEAM_DRAWER, { teamId, data: null, isFetching: true });

    try {
      const { data } = await OperationsAPI.teamAgents({
        ...buildParams($state.period),
        team_id: teamId,
      });
      commit(types.SET_TEAM_DRAWER_DATA, { teamId, data });
    } catch (error) {
      // Sem dado anterior pra preservar aqui (o drawer acabou de abrir): o
      // drawer mostra o erro e a página dispara o toast pelo lastError.
      commit(types.SET_TEAM_DRAWER_DATA, { teamId, data: null });
      commit(types.SET_UI_FLAG, {
        lastError: { message: errorMessage(error), at: Date.now() },
      });
    }
  },

  closeTeamDrawer({ commit }) {
    commit(types.SET_TEAM_DRAWER, initialTeamDrawer());
  },

  // Chamado pelo actionCable a cada evento. Só age com a página montada e no
  // modo Tempo Real — Hoje/Período são consultas estáticas.
  realtimeNudge({ state: $state, dispatch }, source) {
    if (!$state.active) return;
    if ($state.period.mode !== 'realtime') return;

    const blocks = SOURCE_BLOCKS[source];
    if (!blocks) return;

    const now = Date.now();
    const elapsed = now - (throttle.lastAt[source] || 0);
    // Lote ainda no ar = NÃO empilha outro por cima: o throttle espaça os
    // inícios, mas quem protege o backend é esperar o lote anterior TERMINAR.
    // Sem isso, servidor lento vira bola de neve (requests acumulam mais
    // rápido do que terminam) e derruba o app inteiro, não só o painel.
    const busy = $state.uiFlags.isFetching;

    if (!busy && elapsed >= NUDGE_WINDOW_MS) {
      throttle.lastAt[source] = now;
      dispatch('fetchBlocks', blocks);
      return;
    }

    if (throttle.timers[source]) return;

    // O timer re-despacha o próprio nudge (não o fetch): na hora de disparar,
    // todos os guards — página montada, modo Tempo Real, lote em voo — são
    // reavaliados; se ainda estiver ocupado, re-arma em vez de disparar.
    const delay = busy ? NUDGE_WINDOW_MS : NUDGE_WINDOW_MS - elapsed;
    throttle.timers[source] = setTimeout(() => {
      delete throttle.timers[source];
      dispatch('realtimeNudge', source);
    }, delay);
  },
};

const mutations = {
  [types.SET_OVERVIEW]($state, overview) {
    $state.overview = overview;
  },
  [types.SET_COVERAGE]($state, coverage) {
    $state.coverage = coverage ? { ...coverage } : null;
  },
  [types.SET_AI]($state, ai) {
    $state.ai = ai;
  },
  // Shape do endpoint: { inboxes: [...], voice }. `voice` null = conta sem
  // telefonia (o card não aparece); { error: true } = supervisor fora do ar,
  // e aí o card grita em vez de mostrar zeros.
  [types.SET_CHANNELS]($state, channels) {
    $state.channels = {
      inboxes: [...channels.inboxes],
      voice: channels.voice,
      telephony: channels.telephony,
    };
  },
  [types.SET_TEAMS]($state, teams) {
    $state.teams = [...(teams || [])];
  },
  [types.SET_QUEUE]($state, queue) {
    $state.queue = queue;
  },
  [types.SET_PERIOD]($state, period) {
    $state.period = { ...$state.period, ...period };
  },
  [types.SET_UI_FLAG]($state, flags) {
    $state.uiFlags = { ...$state.uiFlags, ...flags };
  },
  [types.SET_ACTIVE]($state, active) {
    $state.active = active;
  },
  [types.SET_TEAM_DRAWER]($state, drawer) {
    $state.teamDrawer = { ...drawer };
  },
  // Guarda de corrida: se o usuário trocou de time (ou fechou) enquanto a
  // resposta voltava, a resposta velha é DESCARTADA — pintar o drawer com os
  // agentes do time errado seria pior que não atualizar.
  [types.SET_TEAM_DRAWER_DATA]($state, { teamId, data }) {
    if ($state.teamDrawer.teamId !== teamId) return;

    $state.teamDrawer = { teamId, data, isFetching: false };
  },
};

export { state, getters, actions, mutations };

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
